import { useEffect, useState } from "react";

import { Bot, KeyRound } from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

import { useAiSettings, useUpdateAiSettings, type AiSettingsStatus } from "../api";

/**
 * Superuser-only AI provider settings for the Reports agent: enter a Claude
 * (Anthropic) and/or OpenAI (ChatGPT / Codex) API key and a default model.
 * Keys are stored encrypted at rest and never returned — the page only shows
 * whether each provider is configured. The local Ollama models on Spark are the
 * default and need no key; these are for using a cloud model instead.
 */
export function AiSettingsPage() {
  const status = useAiSettings();
  const update = useUpdateAiSettings();

  const save = async (patch: Parameters<typeof update.mutateAsync>[0], ok: string) => {
    try {
      await update.mutateAsync(patch);
      toast.success(ok);
    } catch (e) {
      toast.error((e as Error)?.message ?? "Couldn’t save the setting");
    }
  };

  const s = status.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 pb-10">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Bot className="h-6 w-6 text-brand" />
          AI Settings
        </h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Connect a cloud LLM for the Reports agent. The local models on Spark are the
          default and need no key — add a key here only to offer Claude or ChatGPT/Codex as
          a model option. Keys are encrypted at rest and never shown again.
        </p>
      </header>

      {status.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : status.isError ? (
        <Alert variant="danger">
          <AlertTitle>Couldn’t load settings</AlertTitle>
          <AlertDescription>{(status.error as Error)?.message ?? "Unknown error."}</AlertDescription>
        </Alert>
      ) : s ? (
        <>
          {!s.secret_store_ready && (
            <Alert variant="warning">
              <AlertTitle>Encrypted store not configured</AlertTitle>
              <AlertDescription>
                Set <code>CINCH_OPS_SETTINGS_SECRET</code> on the server to save keys in-app.
                Until then, keys can only be provided via the server environment.
              </AlertDescription>
            </Alert>
          )}

          <ProviderCard
            title="Claude (Anthropic)"
            blurb="Uses the Messages API with tool use — great for SQL authoring."
            keyPlaceholder="sk-ant-…"
            modelPlaceholder="claude-opus-4-8"
            modelHint="A Claude model id, e.g. claude-opus-4-8, claude-sonnet-5."
            configured={s.claude_configured}
            source={s.claude_key_source}
            model={s.claude_default_model}
            secretReady={s.secret_store_ready}
            busy={update.isPending}
            onSaveKey={(key) => save({ anthropic_api_key: key }, "Claude key saved")}
            onRemoveKey={() => save({ anthropic_api_key: "" }, "Claude key removed")}
            onSaveModel={(model) => save({ claude_default_model: model }, "Claude default model saved")}
          />

          <ProviderCard
            title="OpenAI (ChatGPT / Codex)"
            blurb="Uses the Chat Completions API with tool use."
            keyPlaceholder="sk-…"
            modelPlaceholder="gpt-5"
            modelHint="An OpenAI model id, e.g. gpt-5, gpt-4.1, gpt-4o."
            configured={s.openai_configured}
            source={s.openai_key_source}
            model={s.openai_default_model}
            secretReady={s.secret_store_ready}
            busy={update.isPending}
            onSaveKey={(key) => save({ openai_api_key: key }, "OpenAI key saved")}
            onRemoveKey={() => save({ openai_api_key: "" }, "OpenAI key removed")}
            onSaveModel={(model) => save({ openai_default_model: model }, "OpenAI default model saved")}
          />
        </>
      ) : null}
    </div>
  );
}

function ProviderCard({
  title,
  blurb,
  keyPlaceholder,
  modelPlaceholder,
  modelHint,
  configured,
  source,
  model,
  secretReady,
  busy,
  onSaveKey,
  onRemoveKey,
  onSaveModel,
}: {
  title: string;
  blurb: string;
  keyPlaceholder: string;
  modelPlaceholder: string;
  modelHint: string;
  configured: boolean;
  source: AiSettingsStatus["claude_key_source"];
  model: string | null;
  secretReady: boolean;
  busy: boolean;
  onSaveKey: (key: string) => Promise<void>;
  onRemoveKey: () => Promise<void>;
  onSaveModel: (model: string) => Promise<void>;
}) {
  const [keyInput, setKeyInput] = useState("");
  const [modelInput, setModelInput] = useState(model ?? "");
  // Keep the model field in sync when the status refetches.
  useEffect(() => setModelInput(model ?? ""), [model]);

  const saveKey = async () => {
    const k = keyInput.trim();
    if (!k) return;
    await onSaveKey(k);
    setKeyInput("");
  };

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            {title}
          </h2>
          <p className="text-xs text-muted-foreground">{blurb}</p>
        </div>
        {configured ? (
          <Badge variant="success">
            Configured{source === "env" ? " · env" : source === "app" ? " · in-app" : ""}
          </Badge>
        ) : (
          <Badge variant="neutral">Not configured</Badge>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">API key</Label>
        <div className="flex items-center gap-2">
          <Input
            type="password"
            autoComplete="off"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder={configured ? "•••••••• (enter a new key to replace)" : keyPlaceholder}
            disabled={!secretReady || busy}
          />
          <Button onClick={() => void saveKey()} disabled={!secretReady || busy || !keyInput.trim()}>
            Save key
          </Button>
          {configured && source === "app" && (
            <Button variant="ghost" onClick={() => void onRemoveKey()} disabled={busy}>
              Remove
            </Button>
          )}
        </div>
        {!secretReady && (
          <p className="text-[11px] text-muted-foreground">
            Saving is disabled until the encrypted store is configured.
          </p>
        )}
        {source === "env" && (
          <p className="text-[11px] text-muted-foreground">
            Currently using the key from the server environment. Saving a key here overrides it.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Default model</Label>
        <div className="flex items-center gap-2">
          <Input
            value={modelInput}
            onChange={(e) => setModelInput(e.target.value)}
            placeholder={modelPlaceholder}
            disabled={busy}
          />
          <Button
            variant="outline"
            onClick={() => void onSaveModel(modelInput.trim())}
            disabled={busy}
          >
            Save
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">{modelHint}</p>
      </div>
    </Card>
  );
}
